import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Plus,
	Pencil,
	Trash2,
	Plug,
	Route as RouteIcon,
	BarChart3,
	CheckCircle2,
	XCircle,
	RefreshCw,
	Loader2,
	Server,
	Cloud,
	Bot,
	Sparkles,
	Search,
	UserPlus,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "../../components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";

const ESTRATEGIAS = [
	{ value: "priority", rotulo: "Ordem que eu definir" },
	{ value: "speed", rotulo: "O mais rápido" },
	{ value: "cost", rotulo: "O mais barato" },
	{ value: "availability", rotulo: "O que está no ar" },
	{ value: "roundRobin", rotulo: "Alternar entre eles" },
];

const SETTING_USE_AI_KEY = "perabitIntelligenceUseAI";
const USE_AI_DEFAULT = true;

const extrairUseAI = (allSettings) => {
	const arr = Array.isArray(allSettings)
		? allSettings
		: allSettings && typeof allSettings === "object"
		? Object.values(allSettings)
		: [];
	for (const row of arr) {
		if (row && typeof row === "object" && row.key === SETTING_USE_AI_KEY) {
			return String(row.value ?? "") === "true";
		}
	}
	return USE_AI_DEFAULT;
};

const AiHub = () => {
	const [aba, setAba] = useState("provedores");
	const [providers, setProviders] = useState([]);
	const [disponiveis, setDisponiveis] = useState([]);
	const [rotas, setRotas] = useState([]);
	const [agentes, setAgentes] = useState([]);
	const [consumo, setConsumo] = useState(null);
	const [useAI, setUseAI] = useState(USE_AI_DEFAULT);
	const [salvandoUseAI, setSalvandoUseAI] = useState(false);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);
	const [coachCfg, setCoachCfg] = useState(null);
	const [salvandoCoach, setSalvandoCoach] = useState(false);

	const [modal, setModal] = useState(null);
	const [form, setForm] = useState({});

	const [coachContatos, setCoachContatos] = useState([]);
	const [coachContatosPag, setCoachContatosPag] = useState(1);
	const [coachContatosBusca, setCoachContatosBusca] = useState("");
	const [coachContatosTemMais, setCoachContatosTemMais] = useState(false);
	const [coachContatosCarregando, setCoachContatosCarregando] = useState(false);
	const [coachContatosAcao, setCoachContatosAcao] = useState(false);
	const [coachContatosSelecionados, setCoachContatosSelecionados] = useState(new Set());
	const [coachContatosTotal, setCoachContatosTotal] = useState(0);
	const [coachSelecionandoTodos, setCoachSelecionandoTodos] = useState(false);
	const [coachContatosTipo, setCoachContatosTipo] = useState("contatos");

	// Modelos oferecidos pelo provedor do formulário aberto.
	const [modelos, setModelos] = useState([]);
	const [buscandoModelos, setBuscandoModelos] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);

	const carregarCoachCfg = async () => {
		try {
			const { data } = await api.get("/ai/coach/config");
			setCoachCfg(data);
		} catch {
			setCoachCfg(null);
		}
	};

	const carregar = useCallback(async () => {
		try {
			const [lista, cat, r, u, ag, cfg] = await Promise.allSettled([
				api.get("/ai/providers"),
				api.get("/ai/available-providers"),
				api.get("/ai/routes"),
				api.get("/ai/usage"),
				api.get("/ai/agents"),
				api.get("/settings"),
			]);
			if (lista.status === "fulfilled") setProviders(lista.value?.data || []);
			if (cat.status === "fulfilled") setDisponiveis(cat.value?.data || []);
			if (r.status === "fulfilled") setRotas(r.value?.data || []);
			if (u.status === "fulfilled") setConsumo(u.value?.data || null);
			if (ag.status === "fulfilled") setAgentes(ag.value?.data || []);
			if (cfg.status === "fulfilled") setUseAI(extrairUseAI(cfg.value?.data));
			await carregarCoachCfg();
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	// Conexão já salva tem chave no servidor: dá para trazer a lista assim que o
	// formulário abre, sem a pessoa precisar pedir. Em cadastro novo a lista só
	// existe depois da chave, então fica vazia até o botão ser clicado.
	useEffect(() => {
		if (modal !== "provedor") {
			setModelos([]);
			return;
		}

		if (form.id) listarModelos({ silencioso: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal, form.id]);

	useEffect(() => {
		if (modal !== "coach-contacts") return;
		const t = setTimeout(
			() => carregarCoachContatos({ resetar: true, busca: coachContatosBusca }),
			400
		);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coachContatosBusca, modal === "coach-contacts"]);

	useEffect(() => {
		if (modal !== "coach-contacts") return;
		setCoachContatosSelecionados(new Set());
		carregarCoachContatos({ resetar: true, busca: coachContatosBusca });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coachContatosTipo, modal === "coach-contacts"]);

	const salvarProvedor = async () => {
		setSalvando(true);
		try {
			const corpo = {
				name: form.name,
				apiKey: form.apiKey || undefined,
				baseUrl: form.baseUrl || undefined,
				defaultModel: form.defaultModel || undefined,
				priority: Number(form.priority) || 100,
			};

			if (form.id) {
				await api.put(`/ai/providers/${form.id}`, corpo);
			} else {
				await api.post("/ai/providers", { ...corpo, provider: form.provider });
			}

			toast.success("Provedor salvo.");
			setModal(null);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const testar = async (provider) => {
		setSalvando(true);
		try {
			const { data } = await api.post(`/ai/providers/${provider.id}/test`);
			if (data.ok) {
				toast.success(`${provider.name} respondeu em ${data.latencyMs}ms.`);
			} else {
				toast.error(`${provider.name}: ${data.error}`);
			}
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	// Modelos que o provedor oferece agora, para o campo "modelo padrão" ser uma
	// escolha e não um nome digitado de cabeça. Funciona antes de salvar: a
	// chave recém-colada vai junto na consulta.
	const listarModelos = async ({ silencioso } = {}) => {
		if (!form.provider && !form.id) return;

		setBuscandoModelos(true);
		try {
			const { data } = await api.post("/ai/providers/available-models", {
				providerId: form.id,
				provider: form.provider,
				apiKey: form.apiKey || undefined,
				baseUrl: form.baseUrl || undefined,
			});

			setModelos(data || []);

			if (!silencioso) {
				toast.success(`${(data || []).length} modelo(s) disponível(is).`);
			}
		} catch (err) {
			setModelos([]);
			// No carregamento automático o erro é esperado (chave ainda não
			// colada); avisar ali seria barulho a cada abertura do formulário.
			if (!silencioso) toastError(err);
		}
		setBuscandoModelos(false);
	};

	const buscarModelos = async (provider) => {
		setSalvando(true);
		try {
			const { data } = await api.post(
				`/ai/providers/${provider.id}/sync-models`
			);
			toast.success(`${data.length} modelo(s) encontrado(s).`);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const salvarUseAI = async (novoValor) => {
		if (salvandoUseAI) return;
		setSalvandoUseAI(true);
		try {
			await api.put(`/settings/${SETTING_USE_AI_KEY}`, {
				value: novoValor ? "true" : "false",
			});
			setUseAI(Boolean(novoValor));
			toast.success(
				novoValor
					? "Perabit Intelligence passará a usar esta infraestrutura de IA."
					: "Perabit Intelligence usará apenas dados reais do banco, sem chamadas à IA."
			);
		} catch (err) {
			toastError(err);
			setUseAI(!novoValor ? false : USE_AI_DEFAULT);
		}
		setSalvandoUseAI(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/ai/providers/${selecionado.id}`);
			toast.success("Provedor removido.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	const salvarAgente = async () => {
		setSalvando(true);
		try {
			const corpo = {
				name: form.name,
				prompt: form.prompt,
				aiProviderId: form.aiProviderId ? Number(form.aiProviderId) : null,
				model: form.model || null,
				temperature: Number(form.temperature ?? 0.7),
				maxMessages: Number(form.maxMessages ?? 5),
				isActive: form.isActive !== false,
			};

			if (form.id) {
				await api.put(`/ai/agents/${form.id}`, corpo);
			} else {
				await api.post("/ai/agents", corpo);
			}

			toast.success("Agente salvo.");
			setModal(null);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluirAgente = async () => {
		try {
			await api.delete(`/ai/agents/${selecionado.id}`);
			toast.success("Agente removido.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	const salvarRota = async () => {
		setSalvando(true);
		try {
			await api.put(`/ai/routes/${form.feature}`, {
				aiProviderId: form.aiProviderId ? Number(form.aiProviderId) : null,
				model: form.model || null,
				strategy: form.strategy,
				fallbackChain: form.fallbackChain || [],
			});
			toast.success("Roteamento salvo.");
			setModal(null);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const salvarCoach = async () => {
		if (!coachCfg) return;
		setSalvandoCoach(true);
		try {
			const body = {
				enabled: !!coachCfg.enabled,
				scope: coachCfg.scope === "selected" ? "selected" : "all",
				maxDailyRuns: coachCfg.maxDailyRuns ?? "",
				debounceSeconds: coachCfg.debounceSeconds ?? "",
			};
			await api.put("/ai/coach/config", body);
			toast.success("Configuração salva.");
			await carregar();
		} catch (e) {
			toastError(e);
		} finally {
			setSalvandoCoach(false);
		}
	};

	const carregarCoachContatos = async ({ pagina = 1, busca = "", resetar = true } = {}) => {
		try {
			setCoachContatosCarregando(true);
			const params = {
				searchParam: busca,
				pageNumber: String(pagina),
			};
			if (coachContatosTipo !== "todos") {
				params.tipo = coachContatosTipo;
			}
			const { data } = await api.get("/contacts", { params });
			if (resetar) {
				setCoachContatos(data.contacts || []);
				setCoachContatosTotal(data.count || 0);
				setCoachContatosSelecionados(new Set());
				setCoachContatosPag(1);
			} else {
				setCoachContatos((prev) => [...prev, ...(data.contacts || [])]);
				setCoachContatosPag(pagina);
			}
			setCoachContatosTemMais(Boolean(data.hasMore));
		} finally {
			setCoachContatosCarregando(false);
		}
	};

	const selecionarTodosEncontrados = async ({ marcar = true } = {}) => {
		try {
			setCoachSelecionandoTodos(true);
			const params = { searchParam: coachContatosBusca };
			if (coachContatosTipo !== "todos") {
				params.tipo = coachContatosTipo;
			}
			const { data } = await api.get("/contacts/ids", { params });
			const ids = Array.isArray(data?.ids) ? data.ids : [];
			const tipoLabel =
				coachContatosTipo === "grupos"
					? " grupos"
					: coachContatosTipo === "todos"
					? " contatos/grupos"
					: " contatos";
			if (marcar) {
				setCoachContatosSelecionados(new Set(ids));
				toast.success(`${ids.length}${tipoLabel} selecionados de TODAS as páginas.`);
			} else {
				setCoachContatosSelecionados((prev) => {
					const n = new Set(prev);
					ids.forEach((id) => n.delete(id));
					return n;
				});
				toast.success(`${ids.length}${tipoLabel} removidos da seleção.`);
			}
		} catch (e) {
			toastError(e);
		} finally {
			setCoachSelecionandoTodos(false);
		}
	};

	const aplicarBulkCoach = async (enable) => {
		const ids = Array.from(coachContatosSelecionados);
		if (!ids.length) return;
		try {
			setCoachContatosAcao(true);
			await api.post("/contacts/bulk-enable-coach", { contactIds: ids, enable });
			toast.success(
				enable
					? `${ids.length} contatos HABILITADOS no Coach.`
					: `${ids.length} contatos DESABILITADOS no Coach.`
			);
			await carregarCoachContatos({ resetar: true, busca: coachContatosBusca });
			try {
				const r = await api.get("/ai/coach/config");
				if (r?.data) setCoachCfg(r.data);
			} catch (_) {}
			setCoachContatosSelecionados(new Set());
		} catch (e) {
			toastError(e);
		} finally {
			setCoachContatosAcao(false);
		}
	};

	const RenderCoach = ({ cfg, salvando, onSalvar, onChangeCfg }) => {
		if (cfg === null) {
			return (
				<div className="flex items-center justify-center py-20 text-muted-foreground text-xs">
					<Loader2 className="h-5 w-5 mr-2 animate-spin" />
					Carregando configuração do Coach...
				</div>
			);
		}

		const descricaoRota =
			cfg.route && cfg.provider
				? `Rota Coach → ${cfg.provider.name} · ${
						cfg.route.model || cfg.provider.defaultModel || "(modelo padrão)"
					} · estratégia ${cfg.route.strategy}`
				: "Nenhuma rota 'Coach' configurada. Clique em Salvar para criar automaticamente.";

		return (
			<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
				<Card className="md:col-span-2 xl:col-span-2">
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle>Coach IA</CardTitle>
							<Switch
								id="coach-enabled"
								checked={!!cfg?.enabled}
								onCheckedChange={(v) => onChangeCfg({ enabled: v })}
							/>
						</div>
						<CardDescription>{descricaoRota}</CardDescription>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							Hoje: {cfg?.today?.jobsEnqueued ?? 0} jobs ·{" "}
							{cfg?.today?.alertsCreated ?? 0} alertas · US${" "}
							{Number(cfg?.today?.costCentsUsd ?? 0) / 100} ·{" "}
							{cfg?.today?.tokensIn ?? 0} tokens in / {cfg?.today?.tokensOut ?? 0}{" "}
							tokens out
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Escopo de contatos</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						<label className="flex gap-2 items-center rounded border p-3 bg-card">
							<input
								type="radio"
								checked={cfg?.scope !== "selected"}
								onChange={() => onChangeCfg({ scope: "all" })}
							/>
							<div>
								<p className="font-medium">Todos os contatos</p>
								<p className="text-xs text-muted-foreground">
									Hoje. Mesmo funcionamento atual.
								</p>
							</div>
						</label>
						<label className="flex gap-2 items-center rounded border p-3 bg-card mt-2">
							<input
								type="radio"
								checked={cfg?.scope === "selected"}
								onChange={() => onChangeCfg({ scope: "selected" })}
							/>
							<div>
								<p className="font-medium">Apenas contatos marcados</p>
								<p className="text-xs text-muted-foreground">
									Abra o contato › Editar › ative "Habilitar Coach IA" para cada
									cliente que você quer que o Coach acompanhe. O botão manual ⟳
									SEMPRE roda, independente desta marcação.
								</p>
								{cfg?.scope === "selected" && (
									<p className="text-xs font-medium mt-1">
										Contatos habilitados: {cfg?.contactsSelectedCount ?? "—"}
									</p>
								)}
								{cfg?.scope === "selected" && cfg?.contactsSelectedGroupsCount > 0 && (
									<p className="mt-1 rounded border border-amber-500/30 bg-amber-500/10 p-1.5 text-[10px] text-amber-700 dark:text-amber-300">
										⚠ Atenção: Existem <b>{cfg.contactsSelectedGroupsCount}</b> grupos do WhatsApp com o Coach ligado, mas eles NÃO contam no total de pessoas acima. Use o filtro "Grupos" no modal para desligar o Coach dos grupos também se quiser 0 ativos.
									</p>
								)}
								<Button
									type="button"
									variant="secondary"
									size="sm"
									className="mt-2 w-full"
									onClick={() => {
										onChangeCfg({ scope: "selected" });
										setModal("coach-contacts");
									}}
								>
									<Search className="h-3.5 w-3.5 mr-1.5" />
									Selecionar contatos...
								</Button>
							</div>
						</label>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Ajustes avançados</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="coach-max-daily">
								Triggers automáticos por dia (padrão 5000)
							</Label>
							<Input
								id="coach-max-daily"
								type="number"
								min="0"
								placeholder="5000"
								value={
									(cfg?.maxDailyRuns ?? "") === 5000
										? ""
										: cfg?.maxDailyRuns ?? ""
								}
								onChange={(e) =>
									onChangeCfg({
										maxDailyRuns:
											e.target.value === "" ? "" : Number(e.target.value),
									})
								}
							/>
							<p className="text-xs text-muted-foreground">
								0 = sem limite. Triggers manuais (botão ⟳) sempre passam.
							</p>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="coach-deb">
								Segundos sem novo trigger por ticket (padrão 45)
							</Label>
							<Input
								id="coach-deb"
								type="number"
								min="0"
								placeholder="45"
								value={
									(cfg?.debounceSeconds ?? "") === 45
										? ""
										: cfg?.debounceSeconds ?? ""
								}
								onChange={(e) =>
									onChangeCfg({
										debounceSeconds:
											e.target.value === "" ? "" : Number(e.target.value),
									})
								}
							/>
							<p className="text-xs text-muted-foreground">
								0 = sem coalescer. Cada mensagem dispara 1 rodada.
							</p>
						</div>
					</CardContent>
				</Card>

				<div className="flex justify-end col-span-full">
					<Button onClick={onSalvar} disabled={salvando || !cfg}>
						{salvando && (
							<Loader2 className="h-4 w-4 mr-1 animate-spin" />
						)}
						Salvar configuração
					</Button>
				</div>

				<Card className="xl:col-span-3">
					<CardHeader>
						<CardTitle>Política de uso</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="mb-2">
							<span className="font-semibold">Como o Coach lê a conversa: </span>
							Sempre carrega as 20 mensagens mais recentes do ticket (cliente +
							atendente, incluindo mídia sem texto). Não carrega histórico de
							meses para economizar tokens.
						</p>
						<p className="mb-2">
							<span className="font-semibold">Triggers automáticos: </span>
							Sempre que chega uma mensagem do cliente, ou quando o atendente
							manda mensagem para o cliente, dispara 1 tentativa de job. Dentro
							da janela de debounce (padrão 45s) por ticket, apenas o 1º dispara
							o LLM, os outros são agrupados.
						</p>
						<p className="mb-2">
							<span className="font-semibold">
								Trigger manual (botão ⟳):{" "}
							</span>
							Botão "Rodar Coach Agora" no painel lateral do ticket. PULA
							debounce 45s, PULA limite diário, e PULA escopo de contatos
							selecionados. Sempre funciona. Use quando você quer saber a
							opinião da IA naquele exato momento.
						</p>
						<p className="mb-2">
							<span className="font-semibold">
								Teto diário padrão de 5000 jobs:{" "}
							</span>
							Isto protege contra loops infinitos bugados que poderiam gerar 100k
							jobs e custar 100x mais caro. Quando o expediente normal
							ultrapassar este teto (muito difícil hoje), ele pode ser aumentado
							aqui por empresa. Triggers manuais não contam para este bloqueio.
						</p>
						<p className="mb-2">
							<span className="font-semibold">
								Custos GROQ hoje (exemplo empresa média):{" "}
							</span>
							GROQ = mais barato do mercado. Hoje 1M tokens in ≈ US$0,60 · 1M
							tokens out ≈ US$2,40. Um alerta do Coach custa ~1.500 tokens in +
							1.200 tokens out ≈ US$0,0038 = R$0,02 centavos por alerta. 5000
							alertas no dia ≈ R$100/dia. (valores de exemplo, valores reais na
							aba Consumo).
						</p>
						<p className="mb-0">
							<span className="font-semibold">
								Onde confirmar custo real:{" "}
							</span>
							Tudo é registrado na tabela AiUsage do banco. Aba "Consumo" mostra
							tokens gastos por recurso, e o card "Status do Coach" nesta aba
							mostra o gasto REAL de hoje até o momento (tudo em centavos USD).
						</p>
					</CardContent>
				</Card>

				<Card className="xl:col-span-3 grid col-span-full">
					<CardHeader>
						<CardTitle>Dicas</CardTitle>
					</CardHeader>
					<CardContent>
						<ul className="list-disc pl-5 space-y-1 text-sm">
							<li>
								Ative o modo "contatos selecionados" para usar o Coach SÓ em
								clientes VIP ou complexos (ex.: 10% dos clientes que geram 80%
								do faturamento). Reduz custo ~80%.
							</li>
							<li>
								Mantenha debounce ≥ 30s: múltiplas mensagens em sequência viram
								1 rodada do LLM só, não 10.
							</li>
							<li>
								Sempre que possível, use o botão manual ⟳ só quando você
								realmente quer uma opinião nova.
							</li>
						</ul>
					</CardContent>
				</Card>
			</div>
		);
	};

	const definicao = (chave) => disponiveis.find((d) => d.key === chave);

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Remover ${selecionado.name}?`}
				open={Boolean(confirmOpen)}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={confirmOpen === "agente" ? excluirAgente : excluir}
			>
				{confirmOpen === "agente"
					? "As conexões que usavam este agente param de responder com IA até você escolher outro."
					: "As funcionalidades que usavam este provedor passam a usar outro."}
			</ConfirmationModal>

			<div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						Inteligência Artificial
					</h1>
					<p className="text-xs text-muted-foreground">
						Conecte um ou mais provedores e escolha quem atende cada recurso
					</p>
				</div>
				{aba === "provedores" && (
					<Button
						className="h-9"
						onClick={() => {
							setForm({ provider: "", name: "", priority: 100 });
							setModal("provedor");
						}}
					>
						<Plus className="h-4 w-4" />
						<span className="hidden sm:inline">Conectar IA</span>
						<span className="sm:hidden">Conectar</span>
					</Button>
				)}
				{aba === "agentes" && (
					<Button
						className="h-9"
						onClick={() => {
							setForm({
								name: "",
								prompt: "",
								temperature: 0.7,
								maxMessages: 5,
								isActive: true,
							});
							setModal("agente");
						}}
					>
						<Plus className="h-4 w-4" />
						<span className="hidden sm:inline">Novo agente</span>
						<span className="sm:hidden">Novo</span>
					</Button>
				)}
			</div>

			<PageTabs
				abas={[
					{
						value: "provedores",
						rotulo: "Provedores",
						icone: Plug,
						contador: providers.length,
					},
					{
						value: "agentes",
						rotulo: "Agentes",
						icone: Bot,
						contador: agentes.length,
					},
					{ value: "rotas", rotulo: "Quem faz o quê", icone: RouteIcon },
					{ value: "consumo", rotulo: "Consumo", icone: BarChart3 },
					{ value: "coach", rotulo: "Coach IA", icone: Sparkles },
				]}
				atual={aba}
				onChange={setAba}
			/>

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{/* ---------------- provedores ---------------- */}
				{aba === "provedores" &&
					(providers.length === 0 ? (
						<div className="rounded-xl border bg-card py-16 text-center">
							<Plug className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-3 text-sm font-medium">Nenhuma IA conectada</p>
							<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
								Conecte pelo menos um provedor para usar o copiloto, os resumos
								automáticos e a análise das conversas.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{providers.map((provider) => {
								const def = definicao(provider.provider);
								return (
									<div
										key={provider.id}
										className="flex flex-col rounded-xl border bg-card p-4"
									>
										<div className="flex items-start justify-between gap-2">
											<div className="flex min-w-0 items-start gap-2.5">
												<span
													className={cn(
														"mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
														provider.isDown
															? "bg-destructive/10 text-destructive"
															: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
													)}
												>
													{def?.local ? (
														<Server className="h-4 w-4" />
													) : (
														<Cloud className="h-4 w-4" />
													)}
												</span>
												<div className="min-w-0">
													<p className="truncate font-medium">
														{provider.name}
													</p>
													<p className="truncate text-xs text-muted-foreground">
														{def?.name || provider.provider}
													</p>
												</div>
											</div>

											<span
												className={cn(
													"flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
													provider.isDown
														? "bg-destructive/10 text-destructive"
														: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
												)}
											>
												{provider.isDown ? (
													<XCircle className="h-3 w-3" />
												) : (
													<CheckCircle2 className="h-3 w-3" />
												)}
												{provider.isDown ? "Fora do ar" : "No ar"}
											</span>
										</div>

										<dl className="mt-3 space-y-1 text-xs">
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Modelo padrão</dt>
												<dd className="truncate font-medium">
													{provider.defaultModel || "—"}
												</dd>
											</div>
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Modelos</dt>
												<dd className="font-medium">
													{provider.models?.length || 0}
												</dd>
											</div>
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Ordem</dt>
												<dd className="font-medium tabular-nums">
													{provider.priority}
												</dd>
											</div>
										</dl>

										{provider.isDown && provider.lastError && (
											<p className="mt-2 rounded-md bg-destructive/5 p-2 text-[11px] leading-relaxed text-destructive">
												{provider.lastError}
											</p>
										)}

										<div className="mt-3 flex flex-wrap gap-1 border-t pt-3">
											<Button
												variant="outline"
												size="sm"
												className="h-7 text-xs"
												disabled={salvando}
												onClick={() => testar(provider)}
											>
												Testar
											</Button>
											<Button
												variant="outline"
												size="sm"
												className="h-7 text-xs"
												disabled={salvando}
												onClick={() => buscarModelos(provider)}
											>
												<RefreshCw className="h-3 w-3" />
												Modelos
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="ml-auto h-7 w-7"
												aria-label={`Editar ${provider.name}`}
												onClick={() => {
													setForm({
														id: provider.id,
														provider: provider.provider,
														name: provider.name,
														baseUrl: provider.baseUrl || "",
														defaultModel: provider.defaultModel || "",
														priority: provider.priority,
														apiKey: "",
														hasApiKey: provider.hasApiKey,
													});
													setModal("provedor");
												}}
											>
												<Pencil className="h-3.5 w-3.5" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												aria-label={`Remover ${provider.name}`}
												onClick={() => {
													setSelecionado(provider);
													setConfirmOpen("provedor");
												}}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										</div>
									</div>
								);
							})}
						</div>
					))}

				{/* ---------------- roteamento ---------------- */}
				{/* ---------------- agentes ---------------- */}
				{aba === "agentes" &&
					(agentes.length === 0 ? (
						<div className="rounded-xl border bg-card py-16 text-center">
							<Bot className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-3 text-sm font-medium">Nenhum agente criado</p>
							<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
								Um agente é a instrução que a IA segue ao responder no seu
								lugar. Crie um aqui e escolha-o na conexão, em Conexões ›
								editar › Atendimento.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{agentes.map((agente) => (
								<div
									key={agente.id}
									className="flex flex-col rounded-xl border bg-card p-4"
								>
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<p className="truncate text-sm font-medium">
												{agente.name}
											</p>
											<p className="text-xs text-muted-foreground">
												{agente.provider?.name || "Qualquer provedor"}
												{agente.model ? ` · ${agente.model}` : ""}
											</p>
										</div>
										{agente.isActive ? (
											<CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
										) : (
											<XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
										)}
									</div>

									<p className="mt-3 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
										{agente.prompt}
									</p>

									<div className="mt-3 flex items-center gap-1.5 border-t pt-3">
										<span className="text-xs text-muted-foreground">
											Até {agente.maxMessages || "∞"} resposta(s) por conversa
										</span>
										<Button
											variant="ghost"
											size="icon"
											className="ml-auto h-7 w-7"
											aria-label={`Editar ${agente.name}`}
											onClick={() => {
												setForm({ ...agente });
												setModal("agente");
											}}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-destructive"
											aria-label={`Remover ${agente.name}`}
											onClick={() => {
												setSelecionado(agente);
												setConfirmOpen("agente");
											}}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							))}
						</div>
					))}

				{aba === "rotas" && (
					<div className="space-y-2">
						<p className="mb-3 text-xs leading-relaxed text-muted-foreground">
							Escolha qual IA atende cada recurso. Se ela falhar, o sistema tenta
							a próxima da lista automaticamente.
						</p>

						{rotas.map((rota) => (
							<div
								key={rota.feature}
								className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3"
							>
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-medium">{rota.name}</p>
									<p className="truncate text-xs text-muted-foreground">
										{rota.providerName || "Nenhuma escolhida — usa a primeira disponível"}
										{rota.fallbackChain?.length > 0 &&
											` · ${rota.fallbackChain.length} reserva(s)`}
									</p>
								</div>
								<Button
									variant="outline"
									size="sm"
									className="h-8 shrink-0"
									disabled={providers.length === 0}
									onClick={() => {
										setForm({
											feature: rota.feature,
											name: rota.name,
											aiProviderId: rota.aiProviderId
												? String(rota.aiProviderId)
												: "",
											model: rota.model || "",
											strategy: rota.strategy || "priority",
											fallbackChain: rota.fallbackChain || [],
										});
										setModal("rota");
									}}
								>
									Configurar
								</Button>
							</div>
						))}
					</div>
				)}

				{/* ---------------- consumo ---------------- */}
				{aba === "consumo" && consumo && (
					<div className="space-y-4">
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
							{[
								{ rotulo: "Chamadas", valor: consumo.total.chamadas },
								{
									rotulo: "Tokens",
									valor: consumo.total.tokens.toLocaleString("pt-BR"),
								},
								{
									rotulo: "Custo",
									valor: `US$ ${consumo.total.custo.toFixed(4)}`,
								},
								{
									rotulo: "Salvas por reserva",
									valor: consumo.total.salvasPorFailover,
								},
							].map((item) => (
								<div key={item.rotulo} className="rounded-xl border bg-card p-4">
									<p className="text-xs text-muted-foreground">{item.rotulo}</p>
									<p className="mt-1 text-2xl font-semibold tabular-nums">
										{item.valor}
									</p>
								</div>
							))}
						</div>

						{[
							{ titulo: "Por provedor", dados: consumo.porProvedor },
							{ titulo: "Por recurso", dados: consumo.porFuncionalidade },
							{ titulo: "Por usuário", dados: consumo.porUsuario },
						].map((bloco) => (
							<section key={bloco.titulo} className="rounded-xl border bg-card">
								<header className="border-b p-3 px-4">
									<h3 className="text-sm font-semibold">{bloco.titulo}</h3>
								</header>
								{bloco.dados.length === 0 ? (
									<p className="py-6 text-center text-xs text-muted-foreground">
										Nada registrado ainda.
									</p>
								) : (
									<ul className="divide-y">
										{bloco.dados.slice(0, 6).map((linha) => (
											<li
												key={linha.chave}
												className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm"
											>
												<span className="min-w-0 flex-1 truncate">
													{linha.chave}
												</span>
												<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
													{linha.chamadas} chamada(s)
												</span>
												{linha.latenciaMedia > 0 && (
													<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
														{linha.latenciaMedia}ms
													</span>
												)}
												<span
													className={cn(
														"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
														linha.sucessoPercent >= 90
															? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
															: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
													)}
												>
													{linha.sucessoPercent}% ok
												</span>
											</li>
										))}
									</ul>
								)}
							</section>
						))}
					</div>
				)}

				{/* ---------------- coach ---------------- */}
				{aba === "coach" && (
					<RenderCoach
						cfg={coachCfg}
						salvando={salvandoCoach}
						onSalvar={salvarCoach}
						onChangeCfg={(patch) =>
							setCoachCfg((old) => (old ? { ...old, ...patch } : old))
						}
					/>
				)}
			</div>

			{/* ================= modal do provedor ================= */}
			<Dialog open={modal === "agente"} onOpenChange={() => setModal(null)}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar agente" : "Novo agente"}
						</DialogTitle>
						<DialogDescription>
							A instrução é o que a IA segue ao responder no lugar do
							atendente. Depois é só escolher este agente na conexão.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label htmlFor="agente-nome">Nome</Label>
							<Input
								id="agente-nome"
								value={form.name || ""}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder="Atendente da loja"
							/>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="agente-prompt">Instrução</Label>
							<textarea
								id="agente-prompt"
								rows={5}
								className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
								value={form.prompt || ""}
								onChange={(e) => setForm({ ...form, prompt: e.target.value })}
								placeholder="Você atende a loja X. Responda curto, em português, e nunca prometa prazo de entrega."
							/>
							<p className="text-xs text-muted-foreground">
								Diga o que ele é, como deve falar e o que não pode fazer.
							</p>
						</div>

						<div className="grid gap-3 sm:grid-cols-2">
							<div className="space-y-1.5">
								<Label>Provedor</Label>
								<Select
									value={form.aiProviderId ? String(form.aiProviderId) : "auto"}
									onValueChange={(v) =>
										setForm({
											...form,
											aiProviderId: v === "auto" ? null : Number(v),
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="auto">Qualquer um (ordem de prioridade)</SelectItem>
										{providers.map((p) => (
											<SelectItem key={p.id} value={String(p.id)}>
												{p.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-modelo">Modelo</Label>
								<Input
									id="agente-modelo"
									value={form.model || ""}
									onChange={(e) => setForm({ ...form, model: e.target.value })}
									placeholder="deixe vazio para o padrão"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-temp">Criatividade</Label>
								<Input
									id="agente-temp"
									type="number"
									step="0.1"
									min="0"
									max="2"
									value={form.temperature ?? 0.7}
									onChange={(e) =>
										setForm({ ...form, temperature: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									0 = sempre igual · 1 = mais variado
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-max">Respostas por conversa</Label>
								<Input
									id="agente-max"
									type="number"
									min="0"
									value={form.maxMessages ?? 5}
									onChange={(e) =>
										setForm({ ...form, maxMessages: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									Atingido o limite, a conversa espera um humano. 0 = sem
									limite.
								</p>
							</div>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(null)}>
							Cancelar
						</Button>
						<Button onClick={salvarAgente} disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							{form.id ? "Salvar" : "Criar agente"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={modal === "provedor"} onOpenChange={() => setModal(null)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar conexão" : "Conectar uma IA"}
						</DialogTitle>
						<DialogDescription>
							Você pode conectar várias e usar cada uma para um recurso.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						{!form.id && (
							<div className="space-y-1.5">
								<Label>Qual IA</Label>
								<Select
									value={form.provider}
									onValueChange={(v) => {
										const d = definicao(v);
										// Outra IA, outros modelos: a lista da anterior não vale mais e
										// deixá-la na tela ofereceria nome que este provedor não conhece.
										setModelos([]);
										setForm({
											...form,
											provider: v,
											name: d?.name || "",
											baseUrl: d?.local ? d.baseUrl : "",
											defaultModel: d?.defaultModel || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione" />
									</SelectTrigger>
									<SelectContent>
										{disponiveis.map((d) => (
											<SelectItem key={d.key} value={d.key}>
												{d.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{definicao(form.provider)?.docsUrl && (
									<p className="text-xs text-muted-foreground">
										Pegue a chave em{" "}
										<a
											href={definicao(form.provider).docsUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary underline underline-offset-2"
										>
											{definicao(form.provider).docsUrl.replace(
												/^https?:\/\//,
												""
											)}
										</a>
									</p>
								)}
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="nomeProvedor">Nome desta conexão</Label>
							<Input
								id="nomeProvedor"
								placeholder="Ex.: OpenAI principal"
								value={form.name || ""}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
							/>
						</div>

						{definicao(form.provider)?.requiresApiKey !== false && (
							<div className="space-y-1.5">
								<Label htmlFor="chaveProvedor">Chave de API</Label>
								<Input
									id="chaveProvedor"
									type="password"
									placeholder={
										form.hasApiKey
											? "Deixe vazio para manter a chave atual"
											: "Cole a chave aqui"
									}
									value={form.apiKey || ""}
									onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
								/>
								<p className="text-xs text-muted-foreground">
									A chave fica guardada no servidor e nunca é exibida de volta.
								</p>
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="urlProvedor">Endereço do servidor</Label>
							<Input
								id="urlProvedor"
								placeholder="Deixe vazio para usar o endereço oficial"
								value={form.baseUrl || ""}
								onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
							/>
						</div>

						<div className="grid gap-3 sm:grid-cols-2">
							<div className="space-y-1.5">
								<Label htmlFor="modeloProvedor">Modelo padrão</Label>

								{modelos.length > 0 ? (
									<Select
										value={form.defaultModel || ""}
										onValueChange={(v) =>
											setForm({ ...form, defaultModel: v })
										}
									>
										<SelectTrigger id="modeloProvedor">
											<SelectValue placeholder="Escolha o modelo" />
										</SelectTrigger>
										<SelectContent>
											{/* O que já estava salvo continua na lista mesmo que o
											    provedor tenha parado de anunciá-lo: sem isso o
											    campo apareceria vazio e a conexão seria salva sem
											    modelo nenhum. */}
											{(modelos.some((m) => m.id === form.defaultModel) ||
											!form.defaultModel
												? modelos
												: [{ id: form.defaultModel }, ...modelos]
											).map((m) => (
												<SelectItem key={m.id} value={m.id}>
													{m.label || m.id}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								) : (
									<Input
										id="modeloProvedor"
										placeholder="Busque a lista ou digite o nome"
										value={form.defaultModel || ""}
										onChange={(e) =>
											setForm({ ...form, defaultModel: e.target.value })
										}
									/>
								)}

								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-7 w-full text-xs"
									onClick={() => listarModelos()}
									disabled={buscandoModelos}
								>
									{buscandoModelos ? (
										<Loader2 className="h-3 w-3 animate-spin" />
									) : (
										<RefreshCw className="h-3 w-3" />
									)}
									{modelos.length > 0 ? "Atualizar lista" : "Buscar modelos"}
								</Button>

								<p className="text-xs text-muted-foreground">
									{modelos.length > 0
										? "Os mais recentes aparecem primeiro."
										: "Cole a chave e busque: a lista vem do próprio provedor."}
								</p>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="ordemProvedor">Ordem de uso</Label>
								<Input
									id="ordemProvedor"
									type="number"
									min="1"
									value={form.priority ?? 100}
									onChange={(e) =>
										setForm({ ...form, priority: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									Menor = tentado primeiro.
								</p>
							</div>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(null)}>
							Cancelar
						</Button>
						<Button
							disabled={salvando || !form.provider || !form.name}
							onClick={salvarProvedor}
						>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ================= modal da rota ================= */}
			<Dialog open={modal === "rota"} onOpenChange={() => setModal(null)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{form.name}</DialogTitle>
						<DialogDescription>
							Quem atende este recurso e o que fazer se falhar.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label>IA principal</Label>
							<Select
								value={form.aiProviderId}
								onValueChange={(v) => setForm({ ...form, aiProviderId: v })}
							>
								<SelectTrigger>
									<SelectValue placeholder="Primeira disponível" />
								</SelectTrigger>
								<SelectContent>
									{providers.map((p) => (
										<SelectItem key={p.id} value={String(p.id)}>
											{p.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="modeloRota">Modelo</Label>
							<Input
								id="modeloRota"
								placeholder="Deixe vazio para usar o padrão do provedor"
								value={form.model || ""}
								onChange={(e) => setForm({ ...form, model: e.target.value })}
							/>
						</div>

						<div className="space-y-1.5">
							<Label>Se houver mais de uma opção, usar</Label>
							<Select
								value={form.strategy}
								onValueChange={(v) => setForm({ ...form, strategy: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{ESTRATEGIAS.map((e) => (
										<SelectItem key={e.value} value={e.value}>
											{e.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-1.5">
							<Label>Reservas, na ordem</Label>
							<div className="space-y-1 rounded-lg border p-2">
								{providers
									.filter((p) => String(p.id) !== form.aiProviderId)
									.map((p) => {
										const posicao = (form.fallbackChain || []).indexOf(p.id);
										const escolhida = posicao >= 0;

										return (
											<button
												key={p.id}
												type="button"
												onClick={() =>
													setForm({
														...form,
														fallbackChain: escolhida
															? form.fallbackChain.filter((id) => id !== p.id)
															: [...(form.fallbackChain || []), p.id],
													})
												}
												className={cn(
													"flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
													escolhida
														? "bg-primary/10 text-primary"
														: "hover:bg-muted"
												)}
											>
												<span
													className={cn(
														"flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold",
														escolhida && "border-primary bg-primary text-primary-foreground"
													)}
												>
													{escolhida ? posicao + 1 : ""}
												</span>
												<span className="truncate">{p.name}</span>
											</button>
										);
									})}
								{providers.length <= 1 && (
									<p className="px-2 py-3 text-center text-xs text-muted-foreground">
										Conecte outra IA para ter uma reserva.
									</p>
								)}
							</div>
							<p className="text-xs text-muted-foreground">
								Se a principal falhar, o sistema tenta estas nesta ordem.
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(null)}>
							Cancelar
						</Button>
						<Button disabled={salvando} onClick={salvarRota}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={modal === "coach-contacts"}
				onOpenChange={(abrir) => {
					if (!abrir) setModal(null);
					else setModal("coach-contacts");
				}}
			>
				<DialogContent className="max-h-[88vh] sm:max-w-3xl w-[95vw] overflow-hidden flex flex-col gap-0 p-0">
					<DialogHeader className="px-5 py-4 border-b">
						<DialogTitle className="flex items-center gap-2">
							<Sparkles className="h-5 w-5 text-sky-600" />
							Selecionar contatos para o Coach IA
						</DialogTitle>
						<DialogDescription>
							Quando o escopo Coach estiver em <b>Apenas contatos marcados</b>, só os clientes com
							o Coach HABILITADO (checkbox marcado na lista abaixo) receberão alertas automáticos do
							Coach. O botão ⟳ Rodar Coach agora sempre funciona para qualquer cliente.
						</DialogDescription>
					</DialogHeader>

					<div className="flex items-center gap-2 px-5 py-3 border-b bg-muted/30 flex-wrap">
						<div className="relative flex-1 min-w-[220px]">
							<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
							<Input
								placeholder="Buscar por nome ou número..."
								value={coachContatosBusca}
								onChange={(e) => setCoachContatosBusca(e.target.value)}
								className="pl-9"
							/>
						</div>
						<div className="flex items-center rounded-md border p-0.5 bg-muted/50 text-[11px] shrink-0">
							{[
								{ value: "contatos", label: "Pessoas" },
								{ value: "grupos", label: "Grupos" },
								{ value: "todos", label: "Todos" },
							].map((op) => (
								<button
									key={op.value}
									type="button"
									onClick={() => setCoachContatosTipo(op.value)}
									className={`px-2 py-1 rounded text-[11px] transition-colors ${
										coachContatosTipo === op.value
											? "bg-card shadow-sm font-semibold text-foreground"
											: "text-muted-foreground hover:text-foreground/80"
									}`}
								>
									{op.label}
								</button>
							))}
						</div>
						<Button
							size="sm"
							onClick={() => aplicarBulkCoach(true)}
							disabled={!coachContatosSelecionados.size || coachContatosAcao}
						>
							{coachContatosAcao && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
							Habilitar selecionados
						</Button>
						<Button
							size="sm"
							variant="destructive"
							onClick={() => aplicarBulkCoach(false)}
							disabled={!coachContatosSelecionados.size || coachContatosAcao}
						>
							Desabilitar selecionados
						</Button>
					</div>

					<div className="flex-1 min-h-[320px] max-h-[52vh] overflow-y-auto">
						{coachContatosCarregando && !coachContatos.length ? (
							<div className="flex items-center justify-center h-48">
								<Loader2 className="h-6 w-6 animate-spin text-sky-600" />
							</div>
						) : !coachContatos.length ? (
							<div className="flex flex-col items-center justify-center h-48 gap-1 text-muted-foreground px-5">
								<UserPlus className="h-10 w-10 opacity-40" />
								<p className="text-sm font-medium">Nenhum contato encontrado</p>
								<p className="text-xs">Refine a busca ou importe contatos.</p>
							</div>
						) : (
							<ul className="divide-y">
								<li className="px-5 py-2.5 bg-muted/40 sticky top-0 border-b backdrop-blur-sm z-10 space-y-2">
									<div className="flex items-center gap-3">
										<Checkbox
											checked={
												coachContatos.length > 0 &&
												coachContatos.every((c) => coachContatosSelecionados.has(c.id))
											}
											onCheckedChange={(check) => {
												const novos = new Set(coachContatosSelecionados);
												coachContatos.forEach((c) => {
													if (check) novos.add(c.id);
													else novos.delete(c.id);
												});
												setCoachContatosSelecionados(novos);
											}}
											disabled={coachContatosAcao || coachSelecionandoTodos}
										/>
										<span className="text-[12px] font-medium shrink-0">
											Selecionar página atual ({coachContatos.length})
										</span>
										<div className="ml-auto flex items-center gap-1.5 shrink-0 flex-wrap">
											<Button
												size="sm"
												variant="outline"
												className="h-7 px-2 text-[11px]"
												disabled={coachContatosTotal <= 0 || coachSelecionandoTodos || coachContatosAcao}
												onClick={() => selecionarTodosEncontrados({ marcar: true })}
											>
												{coachSelecionandoTodos && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
												＋ Selecionar TODOS os {coachContatosTotal}
											</Button>
											<Button
												size="sm"
												variant="outline"
												className="h-7 px-2 text-[11px] text-rose-600 hover:text-rose-700"
												disabled={coachContatosTotal <= 0 || coachSelecionandoTodos || coachContatosAcao || coachContatosSelecionados.size === 0}
												onClick={() => selecionarTodosEncontrados({ marcar: false })}
											>
												－ Remover seleção dos {coachContatosTotal}
											</Button>
										</div>
									</div>
									<div className="flex items-center gap-2 text-[11px] text-muted-foreground">
										<span className="font-medium text-foreground/80">{coachContatosSelecionados.size} selecionados</span>
										<span>·</span>
										<span>{coachContatos.filter(c => c.enableCoach).length} coach ON nesta página</span>
										<span>·</span>
										<span>{coachContatosTotal} encontrados no total com este filtro</span>
									</div>
								</li>
								{coachContatos.map((c) => (
									<li
										key={c.id}
										className={`px-5 py-2.5 flex items-center gap-3 hover:bg-muted/30 ${
											coachContatosSelecionados.has(c.id) ? "bg-sky-500/[0.06]" : ""
										}`}
									>
										<Checkbox
											checked={coachContatosSelecionados.has(c.id)}
											onCheckedChange={(v) => {
												const n = new Set(coachContatosSelecionados);
												if (v) n.add(c.id);
												else n.delete(c.id);
												setCoachContatosSelecionados(n);
											}}
											disabled={coachContatosAcao}
										/>
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-2">
												<p className="text-[13px] font-medium truncate">
													{c.name || "(Sem nome)"}
												</p>
												{c.enableCoach ? (
													<Badge
														variant="outline"
														className="border-sky-500/40 text-sky-700 dark:text-sky-300 bg-sky-500/10 text-[10px] px-1.5 py-0"
													>
														Coach ON
													</Badge>
												) : (
													<Badge
														variant="outline"
														className="border-muted text-muted-foreground text-[10px] px-1.5 py-0"
													>
														Coach OFF
													</Badge>
												)}
											</div>
											<p className="text-[11px] text-muted-foreground truncate">
												{c.number || "(Sem número)"}
											</p>
											{c.tags?.length ? (
												<div className="mt-1 flex flex-wrap gap-1">
													{c.tags.slice(0, 4).map((t) => (
														<span
															key={t.id}
															className="text-[10px] px-1.5 py-0 rounded-full border"
															style={{
																borderColor: (t.color || "#ccc") + "55",
																color: t.color,
															}}
														>
															{t.name}
														</span>
													))}
													{c.tags.length > 4 && (
														<span className="text-[10px] text-muted-foreground">
															+{c.tags.length - 4}
														</span>
													)}
												</div>
											) : null}
										</div>
									</li>
								))}
							</ul>
						)}
					</div>

					<DialogFooter className="px-5 py-3 border-t bg-muted/20 items-center">
						<div className="mr-auto flex items-center gap-2 text-[12px] text-muted-foreground">
							{coachContatosTemMais && (
								<Button
									size="sm"
									variant="outline"
									onClick={() =>
										carregarCoachContatos({
											pagina: coachContatosPag + 1,
											busca: coachContatosBusca,
											resetar: false,
										})
									}
									disabled={coachContatosCarregando}
								>
									{coachContatosCarregando && (
										<Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
									)}
									Carregar mais
								</Button>
							)}
							<span>
								{coachCfg?.contactsSelectedCount !== undefined
									? `Total habilitados empresa: ${coachCfg.contactsSelectedCount}`
									: ""}
							</span>
						</div>
						<Button variant="outline" onClick={() => setModal(null)}>
							Fechar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default AiHub;
